/**
 * The Services screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/services` (all optional; a default is never written):
 * - `q`: search on the service name.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends services in no defined order, so the
 *   default is Name ascending.
 *
 * In the grouped view the sort applies WITHIN each category section: the rows are sorted
 * first and then grouped, and grouping keeps each section's rows in input order. A service
 * under several categories is sorted into each of them. The sections themselves keep the
 * categories-list order (Uncategorized last).
 */
import type { V2Service } from '@/api/services';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';
import { SERVICE_STATUS_WORDS, whoSummary } from './serviceLabels';

export const SERVICES_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

/**
 * The list's sortable columns (DNKADM-0493 row: name, status, lengths and prices, members pay
 * with, who, staff approval). An old `sort=` value from a bookmarked URL that is no longer a
 * column falls back to the default sort.
 */
export type ServiceSortKey = 'name' | 'status' | 'variations' | 'bucket' | 'who' | 'approval';
export const SERVICE_SORT_KEYS: readonly ServiceSortKey[] = [
  'name',
  'status',
  'variations',
  'bucket',
  'who',
  'approval',
];
export const SERVICE_DEFAULT_SORT: SortState<ServiceSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a service (its name, falling back to the id). */
export function serviceName(service: V2Service): string {
  return service.name.length > 0 ? service.name : service.id;
}

/** The status chip word (DNKADM-0493: Draft / Turned off / Active). */
export function serviceStatusLabel(status: V2Service['status']): string {
  return SERVICE_STATUS_WORDS[status].label;
}

/**
 * What a service row shows (and sorts by) in each column. `bucketLabel` is the screen's
 * resolver ("Free" / bucket name / "—"); an unresolved bucket ("—") and an unknown
 * variation count sort with the blanks (last).
 */
export function serviceSortValue(
  service: V2Service,
  key: ServiceSortKey,
  bucketLabel: (service: V2Service) => string,
): SortValue {
  switch (key) {
    case 'name':
      return serviceName(service);
    case 'bucket': {
      const label = bucketLabel(service);
      return label === '—' ? null : label;
    }
    case 'who':
      return whoSummary(service);
    case 'approval':
      return service.requiresConfirmation ? 'Yes' : 'No';
    case 'variations':
      return service.variationCount;
    case 'status':
      return serviceStatusLabel(service.status);
  }
}

/** The Services search: the service name. */
export function serviceMatches(service: V2Service, query: string): boolean {
  return matchesSearch(query, [serviceName(service)]);
}
